import { Injectable, Renderer2, RendererFactory2, inject } from "@angular/core";
import { Title, Meta } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { environment } from "src/environments/environment";
import { LanguageService } from "../core/services/language.service";
import { DOCUMENT } from "@angular/common";
import { formatDescription } from "../core/helpers/helpers";
import { REQUEST as SSR_REQUEST } from "ngx-cookie-service-ssr";
import { Request } from "express";
import { ScreenSizeService } from "@app/shared/screen-size.service";
import { SEO } from "../models/shared-models/seo.model";

@Injectable({
  providedIn: "root",
})
export class IndexabilityService {
  screenSizeService = inject(ScreenSizeService);

  private titleService = inject(Title);
  private metaService = inject(Meta);
  private languageService = inject(LanguageService);
  private router = inject(Router);
  private document = inject<Document>(DOCUMENT);
  private request = inject<Request>(SSR_REQUEST, { optional: true });

  private renderer: Renderer2;

  constructor() {
    const rendererFactory = inject(RendererFactory2);
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  setMetaTags(seo?: SEO): void {
    const currentLang = this.languageService.currentLanguage();
    const title = seo?.title;
    const description = seo?.description;

    // If no relativeUrl provided, derive from current route
    const relativeUrl = seo?.relativeUrl ?? this.getCurrentRelativeUrl();

    // Set html lang attribute
    this.renderer.setAttribute(
      this.document.documentElement,
      "lang",
      currentLang,
    );

    // Only set title if provided
    if (title) {
      this.titleService.setTitle(title);
    }

    // Only set description if provided
    if (description) {
      const formattedDesc = formatDescription(description, 160);
      this.metaService.updateTag({
        name: "description",
        content: formattedDesc,
      });
    }

    // Add meta tag for robots
    this.metaService.updateTag({ name: "robots", content: "index, follow" });

    // Add canonical link
    const canonicalLink = this.formatFullURL(relativeUrl, currentLang);
    this.updateCanonicalLink(canonicalLink);

    // Remove all existing hreflang tags at once
    this.removeAllHreflangTags();

    // Add hreflangs for supported site languages
    const langs = environment.supportedLanguages;
    langs.forEach((lang: string) => {
      const alternateLink = this.formatFullURL(relativeUrl, lang);
      this.addHreflangTag(lang, alternateLink);
    });

    // Add x-default
    const defaultLang = "en";
    const defaultLink = this.formatFullURL(relativeUrl, defaultLang);
    this.addHreflangTag("x-default", defaultLink);
  }

  /**
   * Get current route path
   */
  private getCurrentRelativeUrl(): string {
    const currentUrl = this.router.url;
    const currentLang = this.languageService.currentLanguage();

    // Remove language prefix from URL
    const withoutLang = currentUrl.replace(
      new RegExp(`^/${currentLang}/?`),
      "",
    );

    // Remove query params and fragments
    const cleanUrl = withoutLang.split("?")[0].split("#")[0];

    return cleanUrl;
  }

  private updateCanonicalLink(href: string): void {
    const head = this.document.head;
    const existingCanonical = this.document.querySelector(
      `link[rel='canonical']`,
    );
    if (existingCanonical) {
      this.renderer.removeChild(head, existingCanonical);
    }
    const link = this.renderer.createElement("link");
    this.renderer.setAttribute(link, "rel", "canonical");
    this.renderer.setAttribute(link, "href", href);
    this.renderer.appendChild(head, link);
  }

  private removeAllHreflangTags(): void {
    const existingHreflangs = this.document.querySelectorAll(
      'link[rel="alternate"][hreflang]',
    );
    existingHreflangs.forEach((tag) => {
      this.renderer.removeChild(this.document.head, tag);
    });
  }

  private addHreflangTag(hreflang: string, href: string): void {
    const link = this.renderer.createElement("link");
    this.renderer.setAttribute(link, "rel", "alternate");
    this.renderer.setAttribute(link, "hreflang", hreflang);
    this.renderer.setAttribute(link, "href", href);
    this.renderer.appendChild(this.document.head, link);
  }

  private formatFullURL(relativeUrl: string, lang: string): string {
    let baseUrl: string;

    if (this.screenSizeService.isBrowser) {
      baseUrl = location.origin;
    } else if (this.request) {
      // Server-side: extract from request
      const protocol = this.request.protocol;
      const host = this.request.get("host");

      // Force HTTPS for staging/production
      const finalProtocol =
        protocol === "https" ||
        this.request.get("x-forwarded-proto") === "https"
          ? "https"
          : protocol;

      baseUrl = `${finalProtocol}://${host}`;
    } else {
      baseUrl = "";
    }

    // Clean up relativeUrl: remove leading/trailing slashes
    const cleanPath = relativeUrl?.trim().replace(/^\/+|\/+$/g, "") || "";

    // Build URL with consistent slash handling
    return cleanPath ? `${baseUrl}/${lang}/${cleanPath}` : `${baseUrl}/${lang}`;
  }
}
